import type { ReactNode, JSX } from 'react';
import { forwardRef } from 'react';
import { styled } from 'styled-components';
import { isNotNil, isNotUndefined } from '@wistia/type-guards';
import type { ButtonProps } from '../Button';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';

export type MenuItemButtonProps = ButtonProps & {
  /**
   *  Pre-defined visual treatments for the menu item
   */
  appearance?: 'dangerous' | 'default' | 'gated';
  /**
   * Optional `Badge` to display on the MenuItem. Must be an instance of [Badge](../?path=/docs/components-badge--docs)
   */
  badge?: JSX.Element;
  /**
   * The content of the MenuItem
   */
  children?: ReactNode;
  /**
   * Keyboard shortcut to bind to the MenuItem. Won't be shown if `badge` is set.
   * *Note:* This is just visual, it doesn't actually bind the key command.
   */
  command?: ReactNode;
  /**
   * Icon to show before the MenuItem text. Ignored if children is provided or if `appearance` is `gated`
   */
  icon?: ButtonProps['leftIcon'];

  // Storybook doesn't let you exclude props from Subcomponents so we have to ignore them here
  /**
   * @ignore
   */
  leftIcon?: ButtonProps['leftIcon'];
  /**
   * @ignore
   */
  rightIcon?: ButtonProps['rightIcon'];

  // These props can't be omitted from ButtonProps because it causes typescript to be confused
  /**
   * @ignore
   */
  fullWidth?: never;
  /**
   * @ignore
   */
  asChild?: never;
  /**
   * @ignore
   */
  variant?: never;
  /**
   * @ignore
   */
  size?: never;
  /**
   * @ignore
   */
  unstyled?: never;
};

const StyledButton = styled(Button)<{ colorScheme: ColorSchemeTypes; $isGated?: boolean }>`
  ${({ colorScheme }) => getColorScheme(colorScheme)};

  display: flex;
  flex-direction: row;
  gap: var(--menu-item-inner-gap);
  padding: var(--menu-item-padding);
  border-radius: var(--wui-border-radius-01);
  text-align: left;
  color: var(--wui-color-text-button);
  font-size: var(--menu-label-font-size);
  font-weight: var(--menu-label-font-weight);
  line-height: var(--menu-label-line-height);
  transition: background-color var(--wui-motion-duration-01) var(--wui-motion-ease);

  &:active {
    background-color: var(--wui-color-bg-surface-active);
  }

  &[data-highlighted] {
    outline: none;
    ${({ $isGated }) => $isGated && getColorScheme('purple')};
    background-color: var(--wui-color-bg-surface-hover);
  }

  &[aria-disabled='true'] {
    --wui-color-text-button: var(--wui-color-text-disabled);
    --wui-color-text-secondary: var(--wui-color-text-disabled);
    --wui-color-icon: var(--wui-color-icon-disabled);

    &[data-highlighted] {
      background-color: transparent;
    }
  }

  &[role='menuitemradio'] {
    padding-left: var(--wui-space-04);
  }
`;

const StyledLeftIconContainer = styled.div`
  height: var(--menu-item-left-icon-size);
  width: var(--menu-item-left-icon-size);

  && {
    svg {
      height: var(--menu-item-left-icon-size);
      width: var(--menu-item-left-icon-size);
      flex: 0 0 var(--menu-item-left-icon-size);
    }
  }
`;

const StyledRightIconContainer = styled.div`
  height: var(--menu-item-right-icon-size);
  width: var(--menu-item-right-icon-size);

  && {
    svg {
      height: var(--menu-item-right-icon-size);
      width: var(--menu-item-right-icon-size);
      flex: 0 0 var(--menu-item-right-icon-size);
    }
  }
`;

const StyledLabelAndDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--menu-label-description-gap);
  flex-basis: 100%;
`;

const StyledBadgeContainer = styled.div`
  align-self: start;
  justify-self: end;
  font-size: var(--wui-typography-label-4-size);
  color: var(--wui-color-text-secondary);
`;

export const MenuItemButton = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  MenuItemButtonProps
>(({ children, appearance, command, icon, ...props }: MenuItemButtonProps, ref) => {
  let { colorScheme, badge } = props;
  if (appearance === 'dangerous') {
    if (isNotUndefined(colorScheme)) {
      // eslint-disable-next-line no-console
      console.warn('colorScheme prop is ignored when appearance is dangerous');
    }

    colorScheme = 'error';
  }

  if (appearance === 'gated') {
    badge = (
      <Icon
        colorScheme="purple"
        style={{
          height: '16px',
          width: '16px',
        }}
        type="sparkle"
      />
    );
  }

  return (
    <StyledButton
      {...props}
      ref={ref}
      $isGated={appearance === 'gated'}
      colorScheme={colorScheme ?? 'inherit'}
      fullWidth={true}
      unstyled={true}
    >
      {isNotNil(props.leftIcon) ? (
        <StyledLeftIconContainer>{props.leftIcon}</StyledLeftIconContainer>
      ) : null}
      <StyledLabelAndDescriptionContainer>{children}</StyledLabelAndDescriptionContainer>
      {isNotNil(badge) || isNotNil(command) ? (
        <StyledBadgeContainer>{badge ?? command}</StyledBadgeContainer>
      ) : null}
      {isNotNil(props.rightIcon) ? (
        <StyledRightIconContainer>{props.rightIcon}</StyledRightIconContainer>
      ) : null}
    </StyledButton>
  );
});

MenuItemButton.displayName = 'MenuItemButton';
